import { useCallback, useEffect, useState } from "react";
import { Check, AlertTriangle, Link2, Unlink, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { API, apiFetch } from "@/lib/gp";
import { Button } from "@/components/ui/button";

const PLATFORM_LABELS = { reddit: "Reddit", x: "X (Twitter)" };

export default function SocialConnectionsAdmin() {
  const [platforms, setPlatforms] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    apiFetch("/admin/social/status")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setPlatforms(d.platforms || {}))
      .catch(() => toast.error("Could not load social connection status."));
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const social = params.get("social");
    if (social) {
      const [platform, outcome] = social.split("_");
      const label = PLATFORM_LABELS[platform] || platform;
      if (outcome === "connected") toast.success(`${label} connected.`);
      else if (outcome === "cancelled") toast.info(`${label} connect was cancelled.`);
      params.delete("social");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
      window.history.replaceState({}, "", next);
    }
  }, [load]);

  const connect = (platform) => { window.location.href = `${API}/admin/social/${platform}/start`; };

  const disconnect = async (platform) => {
    setBusy(platform);
    try {
      const res = await fetch(`${API}/admin/social/${platform}/disconnect`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error();
      toast.success(`${PLATFORM_LABELS[platform] || platform} disconnected.`);
      load();
    } catch { toast.error("Could not disconnect."); } finally { setBusy(null); }
  };

  return (
    <section className="glass rounded-3xl p-7 mb-8 border border-white/10" data-testid="admin-social-connections">
      <div className="flex items-center gap-2 text-gp-blue"><Share2 size={20} /><span className="font-unbounded font-bold uppercase">Social Connections</span></div>
      <p className="mt-2 max-w-2xl text-sm text-white/55">
        Connect Reddit and X so Yorick's news-board posts and promo cadence cross-post automatically.
        Until a platform is connected, use the manual posting fallback below instead.
      </p>

      {!platforms ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-white/55"><Loader2 size={16} className="animate-spin" /> Checking connections…</div>
      ) : (
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {Object.entries(platforms).map(([platform, status]) => (
            <div key={platform} className="rounded-xl border border-white/10 bg-black/20 p-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold">{PLATFORM_LABELS[platform] || platform}</p>
                {status.connected ? (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-gp-green"><Check size={13} /> Connected{status.connected_by ? ` by ${status.connected_by}` : ""}</p>
                ) : status.configured ? (
                  <p className="mt-1 text-xs text-white/40">Not connected yet</p>
                ) : (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-300"><AlertTriangle size={13} /> App credentials not set</p>
                )}
              </div>
              {status.connected ? (
                <Button size="sm" variant="outline" onClick={() => disconnect(platform)} disabled={busy === platform} className="rounded-full border-white/15 bg-white/5 text-gp-red">
                  {busy === platform ? <Loader2 className="animate-spin mr-1.5" size={13} /> : <Unlink size={13} className="mr-1.5" />} Disconnect
                </Button>
              ) : (
                <Button size="sm" onClick={() => connect(platform)} disabled={!status.configured} className="rounded-full bg-gp-blue">
                  <Link2 size={13} className="mr-1.5" /> Connect
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
